import React, { useCallback, useEffect, useState } from 'react';
import {
  Lock,
  Save,
  LogOut,
  Settings,
} from 'lucide-react';

import {
  api,
  setAdminPassword,
  getAdminPassword,
  clearAdminPassword,
} from '../api';

import { useToast } from '../hooks/use-toast';

const Admin = () => {
  const { toast } = useToast();

  const [auth, setAuth] = useState(false);
  const [pwd, setPwd] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [brandName, setBrandName] = useState('');
  const [homepageTitle, setHomepageTitle] = useState('');
  const [homepageSubtitle, setHomepageSubtitle] = useState('');
  const [instagramPricing, setInstagramPricing] = useState('');
  const [tiktokPricing, setTiktokPricing] = useState('');
  const [whatsappNumber, setWhatsappNumber] = useState('');
  const [instagramUrl, setInstagramUrl] = useState('');
  const [contactEmail, setContactEmail] = useState('');

  const loadSettings = useCallback(async () => {
    setLoading(true);

    try {
      const cfg = await api.adminSettings();

      setBrandName(cfg.brand_name || '');
      setHomepageTitle(cfg.homepage_title || '');
      setHomepageSubtitle(cfg.homepage_subtitle || '');
      setInstagramPricing(cfg.instagram_pricing || '');
      setTiktokPricing(cfg.tiktok_pricing || '');
      setWhatsappNumber(cfg.whatsapp_number || '');
      setInstagramUrl(cfg.instagram_url || '');
      setContactEmail(cfg.contact_email || '');
    } catch (err) {
      if (err?.response?.status === 401) {
        clearAdminPassword();
        setAuth(false);
      }

      toast({
        title: 'Settings load nahi hui',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (getAdminPassword()) {
      setAuth(true);
    }
  }, []);

  useEffect(() => {
    if (auth) {
      loadSettings();
    }
  }, [auth, loadSettings]);

  const login = async (e) => {
    e.preventDefault();

    if (!pwd.trim()) {
      toast({
        title: 'Admin password enter karein',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      await api.adminLogin(pwd);

      setAdminPassword(pwd);
      setAuth(true);
      setPwd('');

      toast({
        title: 'Login successful',
      });
    } catch (err) {
      toast({
        title: 'Wrong admin password',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async (e) => {
    e.preventDefault();

    if (!brandName.trim()) {
      toast({
        title: 'Brand Name required hai',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);

    try {
      await api.updateSettings({
        brand_name: brandName.trim(),
        homepage_title: homepageTitle.trim(),
        homepage_subtitle: homepageSubtitle.trim(),
        instagram_pricing: instagramPricing.trim(),
        tiktok_pricing: tiktokPricing.trim(),
        whatsapp_number: whatsappNumber.trim(),
        instagram_url: instagramUrl.trim(),
        contact_email: contactEmail.trim(),
      });

      toast({
        title: 'Settings saved successfully',
      });
    } catch (err) {
      if (err?.response?.status === 401) {
        clearAdminPassword();
        setAuth(false);
      }

      toast({
        title: 'Settings save nahi hui',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const logout = () => {
    clearAdminPassword();
    setAuth(false);
    setPwd('');

    toast({
      title: 'Logged out',
    });
  };

  if (!auth) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border p-8">
          <div className="flex items-center justify-center mb-6">
            <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center">
              <Lock className="w-7 h-7" />
            </div>
          </div>

          <h1 className="text-2xl font-bold text-center">
            Admin Panel
          </h1>

          <p className="text-sm text-slate-500 text-center mt-2 mb-6">
            Enter your admin password to continue
          </p>

          <form onSubmit={login} className="space-y-4">
            <input
              type="password"
              value={pwd}
              onChange={(e) => setPwd(e.target.value)}
              placeholder="Admin Password"
              autoComplete="current-password"
              className="w-full border rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-slate-300"
            />

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-black text-white rounded-xl px-4 py-3 font-semibold disabled:opacity-50"
            >
              {loading ? 'Logging in...' : 'Login'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-4 py-8">

        {/* Header */}
        <div className="flex items-center justify-between mb-8 gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Settings className="w-6 h-6" />

              <h1 className="text-2xl font-bold">
                Admin Panel
              </h1>
            </div>

            <p className="text-sm text-slate-500 mt-1">
              Website settings
            </p>
          </div>

          <button
            type="button"
            onClick={logout}
            className="flex items-center gap-2 border bg-white rounded-xl px-4 py-2"
          >
            <LogOut className="w-4 h-4" />
            Logout
          </button>
        </div>

        <form
          onSubmit={saveSettings}
          className="bg-white border rounded-2xl shadow-sm p-6 space-y-8"
        >

          {/* Website Information */}
          <div>
            <h2 className="text-lg font-semibold mb-4">
              Website Information
            </h2>

            <div className="space-y-4">

              <div>
                <label className="block text-sm font-medium mb-2">
                  Brand Name
                </label>

                <input
                  type="text"
                  value={brandName}
                  onChange={(e) => setBrandName(e.target.value)}
                  placeholder="Example: IGGrowth"
                  className="w-full border rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-slate-300"
                />

                <p className="text-xs text-slate-500 mt-2">
                  Ye naam website ke visible branding mein use hoga.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Homepage Title
                </label>

                <input
                  type="text"
                  value={homepageTitle}
                  onChange={(e) => setHomepageTitle(e.target.value)}
                  placeholder="Homepage main title"
                  className="w-full border rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-slate-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Homepage Subtitle
                </label>

                <textarea
                  value={homepageSubtitle}
                  onChange={(e) => setHomepageSubtitle(e.target.value)}
                  placeholder="Homepage subtitle"
                  rows={3}
                  className="w-full border rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-slate-300"
                />
              </div>

            </div>
          </div>

          {/* Pricing */}
          <div>
            <h2 className="text-lg font-semibold mb-4">
              Pricing
            </h2>

            <div className="space-y-5">

              {/* Instagram */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  Instagram Pricing
                </label>

                <textarea
                  value={instagramPricing}
                  onChange={(e) => setInstagramPricing(e.target.value)}
                  placeholder={`Starter: 29
Pro: 55`}
                  rows={5}
                  className="w-full border rounded-xl px-4 py-3 font-mono text-sm outline-none focus:ring-2 focus:ring-slate-300"
                />

                <div className="mt-2 rounded-xl bg-slate-50 border p-3">
                  <p className="text-xs font-semibold text-slate-700 mb-1">
                    Format:
                  </p>

                  <p className="text-xs text-slate-600 font-mono whitespace-pre-line">
                    Starter: 29{'\n'}
                    Pro: 55
                  </p>

                  <p className="text-xs text-slate-500 mt-2">
                    Sirf number change karke price update kar sakte hain.
                  </p>
                </div>
              </div>

              {/* TikTok */}
              <div>
                <label className="block text-sm font-medium mb-2">
                  TikTok Pricing
                </label>

                <textarea
                  value={tiktokPricing}
                  onChange={(e) => setTiktokPricing(e.target.value)}
                  placeholder={`Monthly:
Grow: 89
Scale: 149
Elite: 249

Yearly:
Grow: 44
Scale: 74
Elite: 124`}
                  rows={11}
                  className="w-full border rounded-xl px-4 py-3 font-mono text-sm outline-none focus:ring-2 focus:ring-slate-300"
                />

                <div className="mt-2 rounded-xl bg-slate-50 border p-3">
                  <p className="text-xs font-semibold text-slate-700 mb-1">
                    Format:
                  </p>

                  <p className="text-xs text-slate-600 font-mono whitespace-pre-line">
                    Monthly:{'\n'}
                    Grow: 89{'\n'}
                    Scale: 149{'\n'}
                    Elite: 249{'\n'}
                    {'\n'}
                    Yearly:{'\n'}
                    Grow: 44{'\n'}
                    Scale: 74{'\n'}
                    Elite: 124
                  </p>

                  <p className="text-xs text-slate-500 mt-2">
                    Plan names ko same rakhein. Sirf price number change karein.
                  </p>
                </div>
              </div>

            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h2 className="text-lg font-semibold mb-4">
              Contact Information
            </h2>

            <div className="space-y-4">

              <div>
                <label className="block text-sm font-medium mb-2">
                  WhatsApp Number
                </label>

                <input
                  type="text"
                  value={whatsappNumber}
                  onChange={(e) => setWhatsappNumber(e.target.value)}
                  placeholder="Example: 919876543210"
                  className="w-full border rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-slate-300"
                />

                <p className="text-xs text-slate-500 mt-2">
                  Country code ke saath number enter karein.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Instagram URL
                </label>

                <input
                  type="text"
                  value={instagramUrl}
                  onChange={(e) => setInstagramUrl(e.target.value)}
                  placeholder="https://instagram.com/youraccount"
                  className="w-full border rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-slate-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-2">
                  Contact Email
                </label>

                <input
                  type="email"
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                  placeholder="support@example.com"
                  className="w-full border rounded-xl px-4 py-3 outline-none focus:ring-2 focus:ring-slate-300"
                />
              </div>

            </div>
          </div>

          {/* Save */}
          <button
            type="submit"
            disabled={saving || loading}
            className="w-full flex items-center justify-center gap-2 bg-black text-white rounded-xl px-5 py-3 font-semibold disabled:opacity-50"
          >
            <Save className="w-5 h-5" />

            {saving ? 'Saving...' : 'Save Changes'}
          </button>

        </form>
      </div>
    </div>
  );
};

export default Admin;
